import { getMarkRange, type Editor, type Range } from "@tiptap/core";

export function getLinkRangeNearSelection(editor: Editor): void | Range | null {
	const { state } = editor;
	const link = state.schema.marks.link;
	const { from, to } = state.selection;

	// try at $from
	let range = getMarkRange(state.doc.resolve(from), link) ?? (from > 0 ? getMarkRange(state.doc.resolve(from - 1), link) : null);

	// If nothing at from side, try at $to (handles non-collapsed selections)
	if (!range) {
		range = getMarkRange(state.doc.resolve(to), link) ?? (to > 0 ? getMarkRange(state.doc.resolve(to - 1), link) : null);
	}

	return range; // { from, to } or null
}

type LinkSelectionKind =
	| "none" // not on a link at all
	| "at-start" // caret or selection starts exactly at link start
	| "at-end" // caret or selection ends exactly at link end
	| "middle" // inside the link but not touching either boundary
	| "exact" // selection is exactly the whole link
	| "overlaps-left" // selection starts before link start but enters it
	| "overlaps-right"; // selection ends after link end but enters it

export function classifySelectionRelativeToLink(editor: Editor): {
	kind: LinkSelectionKind;
	range?: { from: number; to: number };
} {
	const { state } = editor;
	const { from, to, empty } = state.selection;
	const range = getLinkRangeNearSelection(editor);
	if (!range) return { kind: "none" };

	// Collapsed (caret) cases
	if (empty) {
		if (from === range.from) return { kind: "at-start", range };
		if (from === range.to) return { kind: "at-end", range };
		if (from > range.from && from < range.to) return { kind: "middle", range };
		return { kind: "none" }; // caret outside but adjacent
	}

	// Non-collapsed selection cases (within the same link)
	const insideLeft = from >= range.from;
	const insideRight = to <= range.to;

	if (from === range.from && to === range.to) return { kind: "exact", range };
	if (insideLeft && insideRight) {
		if (from === range.from) return { kind: "at-start", range };
		if (to === range.to) return { kind: "at-end", range };
		return { kind: "middle", range };
	}

	// Partially overlapping selections that extend outside the link
	if (from < range.from && to > range.from && to <= range.to) return { kind: "overlaps-left", range };
	if (from >= range.from && from < range.to && to > range.to) return { kind: "overlaps-right", range };

	return { kind: "none" };
}
